import { Component, OnInit, inject, viewChild, output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { BaseComponent, BaseEditFormComponent, IdDescription } from '../../../../tms-shared';
import {
  ConnectionsReferenceService,
  CurrencyReferenceService,
  LocationReferenceService,
  Supplier,
  SupplierKeyAndName,
  SupplierService,
  TmsFormlyService,
} from 'tms-web-shared';
import { UserService } from '../../services';
import { AuthService } from '../../../../auth';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
  imports: [BaseEditFormComponent],
})
export class UserDetailsComponent extends BaseComponent implements OnInit {
  protected readonly userService = inject(UserService);
  private readonly supplierService = inject(SupplierService);
  private readonly currencyReferenceService = inject(CurrencyReferenceService);
  private readonly locationReferenceService = inject(LocationReferenceService);
  private readonly connectionReferenceService = inject(ConnectionsReferenceService);
  private readonly authService = inject(AuthService);
  private readonly formlyService = inject(TmsFormlyService);

  private readonly form = viewChild(BaseEditFormComponent);
  public readonly saved = output<void>();

  protected fields: FormlyFieldConfig[];

  private isAdmin: boolean;
  private currencies$: Observable<IdDescription[]>;
  private locations$: Observable<IdDescription[]>;
  private connections$: Observable<IdDescription[]>;

  ngOnInit() {
    this.isAdmin = this.authService.isAdmin;
    this.getReferenceData();
    this.getFields();
  }

  private getReferenceData() {
    this.connections$ = this.connectionReferenceService.get();
    this.currencies$ = this.currencyReferenceService.get();
    this.locations$ = this.locationReferenceService.get();
  }

  private getFields() {
    this.fields = this.formlyService.twoColumnGrid(
      [
        this.formlyService.text('firstName', $localize`First Name`, { required: true, maxLength: 100 }),
        this.formlyService.text('lastName', $localize`Last Name`, { required: true, maxLength: 100 }),
        this.formlyService.textReadonly(
          'username',
          $localize`Username`,
          undefined,
          (field: FormlyFieldConfig) => !field.model?.id,
        ),
        this.formlyService.textReadonly(
          'emailAddress',
          $localize`Email Address`,
          undefined,
          (field: FormlyFieldConfig) => !field.model?.id,
        ),
        this.formlyService.text(
          'emailAddress',
          'Email Address',
          { required: true, maxLength: 256 },
          (field: FormlyFieldConfig) => !!field.model?.id,
          undefined,
          [Validators.email],
          undefined,
          {
            notExists: {
              expression: (control: UntypedFormControl) => this.userService.checkEmail(control.value),
            },
          },
        ),
        this.formlyService.checkbox('disabled', $localize`Disabled`, { readonly: !this.isAdmin }),
        this.formlyService.checkbox('amendOtherComments', $localize`Amend Other Users Comments`),
      ],
      [
        ...this.formlyService.supplierSearch(
          'supplierSearch',
          'supplierId',
          undefined,
          (model: Supplier) => this.changeSupplier(model),
          () => this.clearSupplier(),
        ),
        this.formlyService.textReadonly('supplierKey', $localize`Supplier Account`),
        this.formlyService.textReadonly('supplierName', $localize`Supplier`),
        this.formlyService.select('cuttingLocationId', $localize`Cutting Location`, this.locations$),
        this.formlyService.select('connectionId', $localize`Connection`, this.connections$),
        this.formlyService.select('currencyId', $localize`Currency`, this.currencies$),
      ],
    );
  }

  private changeSupplier(model: Supplier) {
    this.supplierService
      .getSupplierKeyAndName(model.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((supplier: SupplierKeyAndName) => this.form().updateModel(supplier));
  }

  private clearSupplier() {
    this.form().updateModel({
      supplierId: undefined,
      supplierKey: undefined,
      supplierName: undefined,
    });
  }
}
